import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { SendEmailModal } from "pages/patient/PatientDocumentsPage/components/SendEmailModal/SendEmailModal";

import { AddSickCertForm } from "components/AddSickCertForm/AddSickCertForm.jsx";
import { SickCertsTable } from "components/AddSickCertForm/components/SickCertsTable/SickCertsTable";
import { ConfirmDialog } from "components/ui/ConfirmDialog/ConfirmDialog";
import { useActiveConsultationSickCert } from "features/consultations/hooks/useActiveConsultationSickCert";
import { useDeleteSickCert } from "features/consultations/hooks/useDeleteSickCert";
import { useDownloadSickCert } from "features/consultations/hooks/useDownloadSickCert";
import { useCurrentPatientId } from "features/patients/hooks/useCurrentPatientId";
import { usePatientData } from "features/patients/hooks/usePatientData";

import styles from "./tabs.module.css";

export const SickCertTab = ({ consultationId, isReadOnly = false }) => {
  const patientId = useCurrentPatientId();
  const { data: patient } = usePatientData();
  const [selectedSickCert, setSelectedSickCert] = useState(null);
  const [emailModalSickCert, setEmailModalSickCert] = useState(null);
  const [deleteDialogSickCert, setDeleteDialogSickCert] = useState(null);

  const [hasInitialised, setHasInitialised] = useState(false);

  const { data: consultationSickCerts, refetch: refetchSickCerts } =
    useActiveConsultationSickCert(consultationId);

  // Default to the most recent cert once, on first load, so the user resumes it.
  // After that, which cert is shown is driven purely by user selection — NOT by
  // the cert list refetching (a save adds a new id; keying on that would remount
  // the form and wipe the editor mid-edit).
  useEffect(() => {
    if (hasInitialised || !consultationSickCerts) return;
    if (consultationSickCerts.length > 0) {
      setSelectedSickCert(consultationSickCerts[0]);
    }
    setHasInitialised(true);
  }, [hasInitialised, consultationSickCerts]);

  const downloadSickCert = useDownloadSickCert();
  const deleteSickCert = useDeleteSickCert();
  const tenantVal = localStorage.getItem("kinesin-tenant");

  const handleDelete = async () => {
    if (!deleteDialogSickCert) {
      return;
    }

    await deleteSickCert.mutateAsync(deleteDialogSickCert);
    setDeleteDialogSickCert(null);

    if (selectedSickCert?.id === deleteDialogSickCert.id) {
      setSelectedSickCert(null);
    }

    refetchSickCerts();
  };

  if (isReadOnly && !consultationSickCerts?.length) {
    return (
      <SickCertsTable
        sickCerts={[]}
        onEdit={() => {}}
        onDownload={() => {}}
        onSendEmail={() => {}}
        onDelete={() => {}}
      />
    );
  }

  // The cert's `documentId` is what /patientemail/send needs to attach
  // the PDF — the modal builds the `documentIds` CSV from the doc ids.
  const emailDocuments =
    emailModalSickCert && emailModalSickCert.documentId
      ? [
          {
            id: emailModalSickCert.documentId,
            name: emailModalSickCert.name || "Sick Certificate",
            documentType: "Sick Certificate",
          },
        ]
      : [];

  const handleOpenSendEmail = (sickCert) => {
    if (!sickCert?.documentId) {
      toast.error(
        "Sick cert PDF isn't ready yet — please save the cert before sending."
      );
      return;
    }
    setEmailModalSickCert(sickCert);
  };

  return (
    <>
      <div className={isReadOnly ? styles.readOnlyContent : undefined}>
        <AddSickCertForm
          key={selectedSickCert?.id || "new"}
          patient={patient}
          patientId={patientId}
          consultationId={consultationId}
          initialData={selectedSickCert || undefined}
          isReadOnly={isReadOnly}
          refetchSickCerts={refetchSickCerts}
          onNewSickCert={() => setSelectedSickCert(null)}
        />
      </div>

      <div className={styles.sectionWrapper}>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>
            Sick Certs for this Consultation
          </h3>
        </div>
        <div className={styles.sectionContent}>
          <SickCertsTable
            sickCerts={consultationSickCerts || []}
            currentSickCertId={
              selectedSickCert?.id || consultationSickCerts?.at(0)?.id
            }
            isReadOnly={isReadOnly}
            onEdit={(sickCert) => setSelectedSickCert(sickCert)}
            onDownload={(sickCert) =>
              downloadSickCert.mutate({
                sickCertId: sickCert.id,
                tenantVal,
                patientId,
              })
            }
            onSendEmail={handleOpenSendEmail}
            onDelete={(sickCert) => setDeleteDialogSickCert(sickCert)}
          />
        </div>
      </div>

      <SendEmailModal
        isOpen={!!emailModalSickCert}
        onClose={() => setEmailModalSickCert(null)}
        patient={patient}
        documents={emailDocuments}
      />

      <ConfirmDialog
        isOpen={!!deleteDialogSickCert}
        onClose={() => setDeleteDialogSickCert(null)}
        onConfirm={handleDelete}
        title="Delete Sick Cert"
        message="Are you sure you want to delete this sick cert? This action cannot be undone."
        confirmLabel="Delete"
        isLoading={deleteSickCert.isPending}
      />
    </>
  );
};
